import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {Navigate, useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import LeftBar from '../../home/components/leftBar'
import {getCurrentColors} from '../../../utils/colors'
import {
    AcademyApiService,
    EstatisticasAcademy,
    USUARIOS_LIMITE_MAX,
    UsuarioAdminAcademy,
    UsuariosAdminAcademy,
} from '../../../services/AcademyApi.service'
import {academyDisponivel} from '../sincronizacao'
import {papelAdmin, usuarioTemEscopoAdmin} from '../adminAuth'
import {AuthContext} from '../../../contexts/auth'
import {
    Overrides,
    Pilula,
    Progresso,
    Secao,
    TITULOS,
    TRILHAS,
    aplicarOverrides,
    carregarSecoesBase,
    contarDominadas,
    lerOverrides,
    lerProgresso,
    licoesDe,
    limparOverridesDaSecao,
    salvarOverrides,
    streakVigente,
    tituloAtual,
    licaoConcluida,
} from '../dominio'

const COLORS = getCurrentColors()

const Page = styled.div`
    display: flex;
    height: 100vh;
    /* mobile: 100dvh acompanha a barra de endereço do navegador */
    height: 100dvh;
    overflow: hidden !important;
`

const Content = styled.main`
    flex: 1;
    overflow-y: auto;
    background-color: ${COLORS.cinzaClaro};
`

const Miolo = styled.div`
    max-width: 960px;
    margin: 0 auto;
    padding: 32px 24px 96px;
    @media (max-width: 640px) {
        padding: 20px 12px 72px;
    }
`

// Tabelas rolam horizontalmente no mobile em vez de estourar a página
const RolagemTabela = styled.div`
    overflow-x: auto;
    & table {
        min-width: 560px;
    }
`

const Cartao = styled.div`
    background-color: ${COLORS.branco};
    border: 1px solid ${COLORS.cinzaDesbotado};
    border-radius: 12px;
    padding: 20px;
`

const GradeCartoes = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 12px;
    margin-top: 16px;
`

function CartaoStat({rotulo, valor}: {rotulo: string; valor: string}) {
    return (
        <Cartao>
            <Typography variant="caption" color="text.secondary">
                {rotulo}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
                {valor}
            </Typography>
        </Cartao>
    )
}

/* --------------------------------------------------- hábito: barra da faixa
   Barra proporcional em CSS puro (sem dependência de gráfico). O número fica
   como texto ao lado — a barra é decorativa e sai da árvore de acessibilidade,
   senão o leitor de tela anunciaria uma div vazia por faixa. */
const LinhaFaixa = styled.div`
    display: grid;
    grid-template-columns: 96px 1fr 64px;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
    @media (max-width: 640px) {
        grid-template-columns: 84px 1fr 48px;
        gap: 8px;
    }
`

const TrilhoBarra = styled.div`
    background-color: ${COLORS.cinzaClaro};
    border: 1px solid ${COLORS.cinzaDesbotado};
    border-radius: 999px;
    height: 10px;
    overflow: hidden;
`

const PreenchimentoBarra = styled.div<{$fracao: number}>`
    background-color: ${COLORS.botaoFormulario};
    height: 100%;
    width: ${(p) => Math.max(0, Math.min(1, p.$fracao)) * 100}%;
`

// Faixas de sequência na ordem do contrato. Fixar a ordem aqui (em vez de
// iterar as chaves do objeto) é o que garante que o bloco não mude de forma
// entre duas leituras, mesmo que a serialização mude de ordem algum dia.
const FAIXAS_STREAK = ['1-2', '3-6', '7-13', '14-29', '30+'] as const

const USUARIOS_POR_PAGINA = 50

// Mesma chave sob a qual o domínio grava o progresso (STORAGE_KEY em
// dominio.ts, privada de lá) — aqui só para comparar a string crua e pular
// re-render quando nada mudou.
const CHAVE_PROGRESSO = 'licito.trilhaGuru.v1'

/* -------------------------------------------------------------- formatação */

// pt-BR usa vírgula decimal. Como o separador do CSV é ';', a vírgula decimal
// não gera ambiguidade nenhuma.
function numeroBr(valor: number, casas = 0): string {
    return valor.toFixed(casas).replace('.', ',')
}

function percentualBr(parte: number, total: number, casas = 1): string {
    if (!total) return '—'
    return `${numeroBr((parte / total) * 100, casas)}%`
}

// Data ISO (YYYY-MM-DD) -> dd/mm/aaaa. Fatiar a string em vez de usar
// `new Date(iso)` é deliberado: o construtor lê a data como UTC e, no fuso de
// Brasília, `2026-08-11` viraria 10/08/2026 na tela.
function dataBr(iso: string | null): string {
    if (!iso) return '—'
    const [ano, mes, dia] = iso.split('-')
    return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso
}

export interface RotuloSecao {
    titulo: string
    trilha: string
}

// O servidor só conhece o slug; o catálogo de conteúdo mora no front. Cruzar
// os dois aqui é o que transforma "pregao-eletronico" em "Pregão eletrônico ·
// Jornada do Guru" — que é o que o gestor consegue ler e agir em cima. Slug
// fora do catálogo (seção despublicada, por exemplo) cai no próprio slug:
// sumir da lista esconderia erro real.
export function rotularSecao(secoes: Secao[], slug: string): RotuloSecao {
    const secao = secoes.find((s) => s.slug === slug)
    if (!secao) return {titulo: slug, trilha: '—'}
    return {
        titulo: secao.titulo,
        trilha:
            TRILHAS.find((t) => t.id === secao.trilha)?.nome ?? secao.trilha,
    }
}

/* --------------------------------------------------------------------- CSV
   Gerado em código, sem dependência nova. Duas decisões são só do Excel em
   pt-BR: separador ';' (o Excel brasileiro usa ',' como decimal, então ','
   não serve de separador de coluna) e BOM UTF-8 no início do arquivo, sem o
   qual ele abre o CSV como Latin-1 e "Revisões" vira "RevisÃµes". */
const BOM_UTF8 = '\ufeff'
const SEPARADOR_CSV = ';'

type CampoCsv = string | number | null | undefined

// Injeção de fórmula (CSV injection). Nome e e-mail vêm do cadastro do core, não
// deste código: um servidor chamado `=HYPERLINK("http://…"&A1,"Clique")` vira
// fórmula VIVA quando o gestor abre a planilha, e `+cmd|'/c calc'!A1` vira DDE.
// O escape de aspas do CSV não protege disso — o Excel remove as aspas do campo
// e avalia o que sobra. A defesa é neutralizar o GATILHO: os caracteres que
// iniciam fórmula no Excel/LibreOffice/Sheets.
const INICIO_DE_FORMULA = /^[=+\-@\t\r]/

// Prefixo apóstrofo: o conteúdo continua legível e completo na célula (nada é
// truncado nem reescrito), mas deixa de ser interpretado como fórmula.
function neutralizarFormula(texto: string): string {
    return INICIO_DE_FORMULA.test(texto) ? `'${texto}` : texto
}

function campoCsv(valor: CampoCsv): string {
    const bruto = valor === null || valor === undefined ? '' : String(valor)
    // Números do próprio painel não passam pela neutralização: um XP negativo
    // ('-5') é número, não fórmula, e virar texto quebraria a soma na planilha.
    const texto = typeof valor === 'number' ? bruto : neutralizarFormula(bruto)
    return /[";\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto
}

export function montarCsv(linhas: CampoCsv[][]): string {
    return (
        BOM_UTF8 +
        linhas.map((l) => l.map(campoCsv).join(SEPARADOR_CSV)).join('\r\n') +
        '\r\n'
    )
}

function baixarCsv(nomeArquivo: string, conteudo: string) {
    const blob = new Blob([conteudo], {type: 'text/csv;charset=utf-8'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = nomeArquivo
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Firefox cancela o download se a URL for revogada no mesmo tick do
    // click() (mesmo racional do `baixar` no CardConquista): revoga com
    // folga. Helper de módulo, sem desmontagem para servir de rede — o blob
    // vive no máximo 60s.
    window.setTimeout(() => URL.revokeObjectURL(url), 60000)
}

// Uma planilha com as métricas da organização, em blocos separados por linha
// em branco — é assim que o gestor cola no relatório de capacitação.
export function csvDasMetricas(
    e: EstatisticasAcademy,
    secoes: Secao[],
    perguntaDaPilula: (id: string) => string | undefined
): string {
    return montarCsv([
        ['Métrica', 'Valor'],
        ['Usuários na trilha', e.usuarios_total],
        ['Ativos hoje', e.ativos_hoje],
        ['Ativos em 7 dias', e.ativos_7d],
        ['Ativos em 30 dias', e.ativos_30d],
        ['XP médio', numeroBr(e.xp_medio, 1)],
        ['Lições concluídas', e.licoes_concluidas_total],
        ['Revisões concluídas', e.revisoes_concluidas_total],
        ['Respostas registradas', e.respostas_total],
        ['Taxa de erro global', `${numeroBr(e.taxa_erro_global * 100, 2)}%`],
        [],
        ['Título', 'Servidores'],
        ...Object.entries(e.distribuicao_titulos).map(([nome, qtd]) => [
            nome,
            qtd,
        ]),
        [],
        ['Faixa de sequência (dias)', 'Servidores'],
        ...FAIXAS_STREAK.map((faixa) => [
            faixa,
            e.distribuicao_streak[faixa] ?? 0,
        ]),
        [],
        ['Seção', 'Trilha', 'Slug', 'Erros', 'Respostas', 'Erro (%)'],
        ...e.secoes_mais_erradas.map((s) => {
            const rotulo = rotularSecao(secoes, s.secao_slug)
            return [
                rotulo.titulo,
                rotulo.trilha,
                s.secao_slug,
                s.erros,
                s.respostas,
                percentualBr(s.erros, s.respostas),
            ]
        }),
        [],
        ['Pílula', 'Pergunta', 'Erros', 'Respostas', 'Erro (%)'],
        ...e.pilulas_mais_erradas.map((p) => [
            p.pilula_id,
            perguntaDaPilula(p.pilula_id) ?? '',
            p.erros,
            p.respostas,
            percentualBr(p.erros, p.respostas),
        ]),
    ])
}

// `user_id` vai numa coluna própria (e não como substituto do nome) para que
// uma linha sem nome continue identificável na planilha sem fingir rótulo.
export function csvDosServidores(itens: UsuarioAdminAcademy[]): string {
    return montarCsv([
        [
            'Nome',
            'E-mail',
            'Identificador',
            'Título',
            'XP',
            'Pílulas dominadas',
            'Sequência atual (dias)',
            'Recorde (dias)',
            'Último estudo',
        ],
        ...itens.map((u) => [
            u.nome ?? '',
            u.email ?? '',
            u.user_id,
            u.titulo,
            u.xp,
            u.dominadas_total,
            u.streak_atual,
            u.streak_recorde,
            u.ultimo_dia ? dataBr(u.ultimo_dia) : '',
        ]),
    ])
}

/* ------------------------------------------------ ordenação dos servidores */

type CampoOrdem =
    'nome' | 'xp' | 'dominadas_total' | 'streak_atual' | 'ultimo_dia'

interface Ordem {
    campo: CampoOrdem
    direcao: 'asc' | 'desc'
}

// XP desc é o padrão porque é exatamente a ordem que o servidor já devolve:
// abrir o painel numa ordem e vê-lo se reorganizar depois seria pior que não
// ordenar.
const ORDEM_PADRAO: Ordem = {campo: 'xp', direcao: 'desc'}

export function ordenarUsuarios(
    itens: UsuarioAdminAcademy[],
    ordem: Ordem
): UsuarioAdminAcademy[] {
    const chave = (u: UsuarioAdminAcademy): string | number => {
        if (ordem.campo === 'nome')
            return (u.nome ?? u.user_id).toLocaleLowerCase('pt-BR')
        // ISO YYYY-MM-DD ordena lexicograficamente; quem nunca estudou ('')
        // fica no fim da ordem decrescente, que é onde se procura por ele.
        if (ordem.campo === 'ultimo_dia') return u.ultimo_dia ?? ''
        return u[ordem.campo]
    }
    return [...itens].sort((a, b) => {
        const va = chave(a)
        const vb = chave(b)
        const c =
            typeof va === 'string' && typeof vb === 'string'
                ? va.localeCompare(vb, 'pt-BR')
                : Number(va) - Number(vb)
        // Desempate SEMPRE por user_id asc, como no servidor. Sem ele, duas
        // pessoas com o mesmo XP trocariam de lugar a cada atualização de 10s.
        if (c === 0) return a.user_id.localeCompare(b.user_id)
        return ordem.direcao === 'asc' ? c : -c
    })
}

const PILULA_VAZIA: Pilula = {
    id: '',
    texto: '',
    pergunta: '',
    alternativas: ['', '', '', ''],
    correta: 0,
    explicacao: '',
    fonte: '',
}

export default function TrilhaGuruAdmin() {
    // `org_admin` vem daqui: o core o devolve em GET /users e o contexto já
    // o mantém para o app inteiro — não há chamada nova por causa do painel.
    const {userInfo} = useContext(AuthContext)
    const navigate = useNavigate()
    const telaPequena = useMediaQuery('(max-width:640px)')
    const [base, setBase] = useState<Secao[] | null>(null)
    const [overrides, setOverrides] = useState<Overrides>(() => lerOverrides())
    const [progresso, setProgresso] = useState<Progresso>(() => lerProgresso())
    const [aba, setAba] = useState<'stats' | 'orgs' | 'conteudo'>('stats')
    // Visão GLOBAL (só master): resumo por organização + usuários cross-org,
    // com drill-down por clique na org. Estados separados dos org-scoped de
    // propósito — carga e paginação independentes.
    const [organizacoes, setOrganizacoes] = useState<
        | import('../../../services/AcademyApi.service').OrganizacoesAcademy
        | null
    >(null)
    const [usuariosGlobais, setUsuariosGlobais] = useState<
        | import('../../../services/AcademyApi.service').UsuariosGlobaisAcademy
        | null
    >(null)
    const [orgFiltrada, setOrgFiltrada] = useState<string | null>(null)
    const [slugSelecionado, setSlugSelecionado] = useState('')
    const [busca, setBusca] = useState('')
    const [emEdicao, setEmEdicao] = useState<Pilula | null>(null)
    const [novaNaSecao, setNovaNaSecao] = useState(false)
    // Métricas agregadas da organização (academy-service); null = sem backend
    const [agregadas, setAgregadas] = useState<EstatisticasAcademy | null>(null)
    // Página do acompanhamento por servidor; null = ainda não chegou nenhuma
    const [usuarios, setUsuarios] = useState<UsuariosAdminAcademy | null>(null)
    const [deslocamento, setDeslocamento] = useState(0)
    const [ordem, setOrdem] = useState<Ordem>(ORDEM_PADRAO)
    const [exportando, setExportando] = useState(false)
    const [erroExportacao, setErroExportacao] = useState(false)

    useEffect(() => {
        let ativo = true
        carregarSecoesBase().then((s) => {
            if (!ativo) return
            setBase(s)
            setSlugSelecionado((atual) => atual || s[0]?.slug || '')
        })
        return () => {
            ativo = false
        }
    }, [])

    // String crua que o useState inicial já parseou: comparar ANTES de
    // parsear é o que evita re-render sem mudança a cada foco — lerProgresso()
    // devolve objeto novo a cada chamada, nunca === ao anterior.
    const progressoCruRef = useRef<string | null>(null)

    // Progresso local: lido na montagem (useState inicial) e relido ao voltar
    // o foco ou quando outra aba grava progresso (evento storage). Sem polling.
    useEffect(() => {
        // sincroniza a ref com o que o estado inicial leu na montagem
        progressoCruRef.current = localStorage.getItem(CHAVE_PROGRESSO)
        const atualizar = () => {
            const cru = localStorage.getItem(CHAVE_PROGRESSO)
            if (cru === progressoCruRef.current) return
            progressoCruRef.current = cru
            setProgresso(lerProgresso())
        }
        window.addEventListener('storage', atualizar)
        window.addEventListener('focus', atualizar)
        return () => {
            window.removeEventListener('storage', atualizar)
            window.removeEventListener('focus', atualizar)
        }
    }, [])

    // Métricas agregadas de TODOS os usuários da organização, direto do
    // academy-service (quando configurado), carregadas ao entrar. O gate
    // repete-se aqui porque effects rodam mesmo quando o render devolve o
    // <Navigate/> — sem isso, quem não é admin dispararia chamadas 403.
    // Em erro transitório mantém o último agregado bom (sem alternar layout).
    useEffect(() => {
        if (!academyDisponivel() || !usuarioTemEscopoAdmin()) return
        // `ativo` descarta resposta obsoleta: uma chamada em voo no desmonte
        // não pode aplicar setState depois do cleanup.
        let ativo = true
        const buscar = () =>
            new AcademyApiService()
                .getEstatisticasAdmin()
                .then((e) => {
                    if (ativo) setAgregadas(e)
                })
                .catch(() => {
                    /* mantém o último valor */
                })
        buscar()
        return () => {
            ativo = false
        }
    }, [])

    // Acompanhamento por servidor, paginado. Mesmo gate e mesma política de
    // erro dos agregados: falha transitória mantém a página que já está na
    // tela em vez de esvaziar a tabela.
    useEffect(() => {
        if (!academyDisponivel() || !usuarioTemEscopoAdmin()) return
        // `ativo` descarta resposta obsoleta: trocar de página re-executa o
        // effect e o cleanup mata a resposta da página anterior ainda em voo —
        // sem isso, a página 1 atrasada sobrescreveria a página 2 já na tela.
        let ativo = true
        const buscar = () =>
            new AcademyApiService()
                .getUsuariosAdmin(USUARIOS_POR_PAGINA, deslocamento)
                .then((u) => {
                    if (ativo) setUsuarios(u)
                })
                .catch(() => {
                    /* mantém a última página boa */
                })
        buscar()
        return () => {
            ativo = false
        }
    }, [deslocamento])

    // Visão global do MASTER. Gate triplo: serviço configurado, papel master
    // (o servidor devolveria 403 para qualquer outro token — o front só evita
    // a chamada fadada ao erro) e a ABA aberta: esta visão atravessa clientes
    // e só carrega quando alguém abre a aba (ou troca o filtro de org).
    useEffect(() => {
        if (!academyDisponivel() || papelAdmin() !== 'master') return
        if (aba !== 'orgs') return
        let ativo = true
        const buscar = () => {
            const api = new AcademyApiService()
            api.getOrganizacoesGlobais()
                .then((o) => {
                    if (ativo) setOrganizacoes(o)
                })
                .catch(() => {
                    /* mantém o último valor */
                })
            api.getUsuariosGlobais(orgFiltrada, USUARIOS_POR_PAGINA, 0)
                .then((u) => {
                    if (ativo) setUsuariosGlobais(u)
                })
                .catch(() => {
                    /* mantém a última página boa */
                })
        }
        buscar()
        return () => {
            ativo = false
        }
    }, [aba, orgFiltrada])

    const secoes = useMemo(
        () => (base ? aplicarOverrides(base, overrides) : []),
        [base, overrides]
    )

    const mutarOverrides = useCallback((novo: Overrides) => {
        salvarOverrides(novo)
        setOverrides(novo)
    }, [])

    /* Derivações memoizadas: o painel re-renderiza a cada resposta e recalcular
       reduce/flatMap/sort sobre o catálogo inteiro em todo render é custo puro.
       Ficam ANTES dos early-returns por regra de hooks — antes da carga
       `secoes` é [] (o useMemo acima já trata base null) e tudo aqui devolve
       zero/vazio sem custo. */
    const totalPilulas = useMemo(
        () => secoes.reduce((a, s) => a + s.pilulas.length, 0),
        [secoes]
    )
    const totalLicoes = useMemo(
        () => secoes.reduce((a, s) => a + licoesDe(s).length, 0),
        [secoes]
    )
    const licoesFeitas = useMemo(
        () =>
            secoes.reduce(
                (a, s) =>
                    a +
                    licoesDe(s).filter((_, i) =>
                        licaoConcluida(progresso, s.slug, i)
                    ).length,
                0
            ),
        [secoes, progresso]
    )
    const porPilula = useMemo(
        () =>
            new Map(
                secoes.flatMap((s) => s.pilulas.map((p) => [p.id, p] as const))
            ),
        [secoes]
    )
    const maisErradas = useMemo(
        () =>
            Object.entries(progresso.stats)
                .filter(([, s]) => s.e > 0)
                .sort((a, b) => b[1].e - a[1].e)
                .slice(0, 10),
        [progresso]
    )

    // Flag exata 'true': env de Vite é string — por truthiness até 'false'
    // habilitaria o módulo.
    if (import.meta.env.VITE_TRILHA_GURU_ENABLED !== 'true') {
        return <Navigate to="/" replace />
    }
    // Acesso: qualquer papel admin entra; o PAPEL decide o que se vê.
    //   master (escopo `admin` no JWT) = tudo, todas as orgs, edição;
    //   org (`users.org_admin`, que chega pelo contexto) = só o engajamento
    //   da própria organização, sem editar nada.
    // O servidor não confia nisto: ele reconfirma o org_admin com o core
    // antes de devolver qualquer número.
    const papel = papelAdmin(Boolean(userInfo?.org_admin))
    if (!usuarioTemEscopoAdmin()) {
        return <Navigate to="/trilha-guru" replace />
    }

    if (!base) {
        return (
            <Page>
                <LeftBar />
                <Content>
                    <Box
                        sx={{display: 'flex', justifyContent: 'center', mt: 12}}
                    >
                        <CircularProgress />
                    </Box>
                </Content>
            </Page>
        )
    }

    const dominadas = contarDominadas(progresso)
    const titulo = tituloAtual(progresso.xp)

    const perguntaDaPilula = (id: string) => porPilula.get(id)?.pergunta

    // A ordenação vale sobre a PÁGINA carregada (o servidor só ordena por XP
    // desc). Com mais de uma página isso é dito na tela, logo abaixo da tabela
    // — ordenar meia lista sem avisar seria mentir sobre o ranking.
    const usuariosDaPagina = usuarios
        ? ordenarUsuarios(usuarios.itens, ordem)
        : []
    const temMaisDeUmaPagina = (usuarios?.total ?? 0) > USUARIOS_POR_PAGINA

    // Escala das barras de hábito: a maior faixa vira 100%. O piso 1 evita
    // dividir por zero quando ninguém tem sequência viva (todas as barras
    // ficam vazias, que é a leitura correta).
    const maiorFaixaStreak = agregadas
        ? Math.max(
              1,
              ...FAIXAS_STREAK.map((f) => agregadas.distribuicao_streak[f] ?? 0)
          )
        : 1

    const alternarOrdem = (campo: CampoOrdem) =>
        setOrdem((atual) =>
            atual.campo === campo
                ? {campo, direcao: atual.direcao === 'asc' ? 'desc' : 'asc'}
                : // Texto começa em A→Z; número e data começam do maior, que é
                  // o que se quer ver primeiro num ranking.
                  {campo, direcao: campo === 'nome' ? 'asc' : 'desc'}
        )

    const baixarMetricas = () => {
        if (!agregadas) return
        baixarCsv(
            'trilha-guru-metricas.csv',
            csvDasMetricas(agregadas, secoes, perguntaDaPilula)
        )
    }

    // O relatório de capacitação é da organização inteira, não da página que
    // está na tela: percorre todas as páginas (no maior lote que o servidor
    // aceita) antes de gerar o arquivo. Se alguma página falhar, não baixa
    // nada — meia lista viraria um relatório errado sem ninguém perceber.
    const baixarServidores = async () => {
        setExportando(true)
        setErroExportacao(false)
        try {
            const api = new AcademyApiService()
            const todos: UsuarioAdminAcademy[] = []
            let pagina = await api.getUsuariosAdmin(USUARIOS_LIMITE_MAX, 0)
            todos.push(...pagina.itens)
            while (todos.length < pagina.total && pagina.itens.length > 0) {
                pagina = await api.getUsuariosAdmin(
                    USUARIOS_LIMITE_MAX,
                    todos.length
                )
                todos.push(...pagina.itens)
            }
            baixarCsv(
                'trilha-guru-servidores.csv',
                csvDosServidores(ordenarUsuarios(todos, ordem))
            )
        } catch {
            setErroExportacao(true)
        } finally {
            setExportando(false)
        }
    }

    const secaoAtiva = secoes.find((s) => s.slug === slugSelecionado)
    const baseAtiva = base.find((s) => s.slug === slugSelecionado)
    const idsBaseAtiva = baseAtiva?.pilulas.map((p) => p.id) ?? []
    const desativadasDaSecao = idsBaseAtiva.filter((id) =>
        overrides.desativadas.includes(id)
    )
    const listaPilulas = [
        ...(secaoAtiva?.pilulas ?? []),
        // desativadas continuam visíveis no painel, para reativar
        // exibe a versão editada mesmo desativada (reativar não pode
        // "ressuscitar" uma edição invisível)
        ...(baseAtiva?.pilulas
            .filter((p) => overrides.desativadas.includes(p.id))
            .map((p) => overrides.editadas[p.id] ?? p) ?? []),
    ].filter(
        (p) =>
            !busca ||
            (p.pergunta + ' ' + p.texto + ' ' + p.id)
                .toLowerCase()
                .includes(busca.toLowerCase())
    )

    const statusDa = (
        p: Pilula
    ): 'original' | 'editada' | 'nova' | 'desativada' => {
        if (overrides.desativadas.includes(p.id)) return 'desativada'
        if (overrides.editadas[p.id]) return 'editada'
        if (
            (overrides.adicionadas[slugSelecionado] ?? []).some(
                (n) => n.id === p.id
            )
        )
            return 'nova'
        return 'original'
    }

    const salvarPilula = (p: Pilula) => {
        const o: Overrides = JSON.parse(JSON.stringify(overrides))
        if (novaNaSecao) {
            o.adicionadas[slugSelecionado] = [
                ...(o.adicionadas[slugSelecionado] ?? []),
                p,
            ]
        } else if (
            (o.adicionadas[slugSelecionado] ?? []).some((n) => n.id === p.id)
        ) {
            o.adicionadas[slugSelecionado] = o.adicionadas[slugSelecionado].map(
                (n) => (n.id === p.id ? p : n)
            )
        } else {
            o.editadas[p.id] = p
        }
        mutarOverrides(o)
        setEmEdicao(null)
        setNovaNaSecao(false)
    }

    const alternarAtivacao = (p: Pilula) => {
        const o: Overrides = JSON.parse(JSON.stringify(overrides))
        const eraNova = (o.adicionadas[slugSelecionado] ?? []).some(
            (n) => n.id === p.id
        )
        if (eraNova) {
            o.adicionadas[slugSelecionado] = o.adicionadas[
                slugSelecionado
            ].filter((n) => n.id !== p.id)
        } else if (o.desativadas.includes(p.id)) {
            o.desativadas = o.desativadas.filter((id) => id !== p.id)
        } else {
            o.desativadas = [...o.desativadas, p.id]
        }
        mutarOverrides(o)
    }

    const exportarSecao = () => {
        if (!secaoAtiva) return
        const conteudo = JSON.stringify(secaoAtiva, null, 2) + '\n'
        const blob = new Blob([conteudo], {type: 'application/json'})
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${secaoAtiva.slug}.json`
        // Firefox exige o anchor NO documento: click() em anchor solto é
        // ignorado e o download não sai.
        document.body.appendChild(a)
        a.click()
        a.remove()
        // revoke adiado, mesmo racional do baixarCsv (Firefox cancela o
        // download se a URL for revogada no mesmo tick do click)
        window.setTimeout(() => URL.revokeObjectURL(url), 60000)
    }

    const descartarAlteracoes = () => {
        if (
            window.confirm(
                'Descartar todas as alterações locais desta seção (edições, desativações e pílulas novas)?'
            )
        ) {
            limparOverridesDaSecao(slugSelecionado, idsBaseAtiva)
            setOverrides(lerOverrides())
        }
    }

    return (
        <Page>
            <LeftBar />
            <Content>
                <Miolo>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <Button
                            onClick={() => navigate('/trilha-guru')}
                            startIcon={<ArrowBackIcon />}
                            color="inherit"
                            size="small"
                        >
                            Trilha
                        </Button>
                        <div>
                            <Typography variant="h5" fontWeight="bold">
                                Trilha do Guru — Administração
                            </Typography>
                            <Typography color="text.secondary">
                                {papel === 'master'
                                    ? 'Estatísticas do Academy e gestão do conteúdo'
                                    : 'Engajamento da sua organização na trilha — somente leitura'}
                            </Typography>
                        </div>
                        {papel === 'org' && (
                            <Chip
                                size="small"
                                label="Somente leitura"
                                sx={{ml: 'auto'}}
                            />
                        )}
                    </Box>

                    {/* Abas só para o master: o admin de organização tem UMA
                        visão (engajamento), e uma barra de abas com aba única
                        seria promessa de navegação que não existe. A aba
                        Conteúdo não é "desabilitada" para a org — não é
                        renderizada, e o painel dela também não (defesa em
                        profundidade logo abaixo). */}
                    {papel === 'master' && (
                        <Tabs
                            value={aba}
                            onChange={(_, v) => setAba(v)}
                            sx={{
                                mt: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                            }}
                        >
                            <Tab
                                value="stats"
                                label="Estatísticas"
                                sx={{textTransform: 'none'}}
                            />
                            <Tab
                                value="orgs"
                                label="Organizações"
                                sx={{textTransform: 'none'}}
                            />
                            <Tab
                                value="conteudo"
                                label="Conteúdo"
                                sx={{textTransform: 'none'}}
                            />
                        </Tabs>
                    )}

                    {aba === 'stats' && (
                        <>
                            {agregadas ? (
                                <>
                                    <Alert severity="success" sx={{mt: 2}}>
                                        Conectado ao{' '}
                                        <strong>academy-service</strong>:
                                        métricas de todos os usuários da sua
                                        organização.
                                    </Alert>
                                    <GradeCartoes>
                                        <CartaoStat
                                            rotulo="Usuários na trilha"
                                            valor={String(
                                                agregadas.usuarios_total
                                            )}
                                        />
                                        <CartaoStat
                                            rotulo="Ativos hoje"
                                            valor={String(
                                                agregadas.ativos_hoje
                                            )}
                                        />
                                        <CartaoStat
                                            rotulo="Ativos em 7 dias"
                                            valor={String(agregadas.ativos_7d)}
                                        />
                                        <CartaoStat
                                            rotulo="Ativos em 30 dias"
                                            valor={String(agregadas.ativos_30d)}
                                        />
                                        <CartaoStat
                                            rotulo="XP médio"
                                            valor={String(
                                                Math.round(agregadas.xp_medio)
                                            )}
                                        />
                                        <CartaoStat
                                            rotulo="Lições concluídas (org)"
                                            valor={String(
                                                agregadas.licoes_concluidas_total
                                            )}
                                        />
                                        <CartaoStat
                                            rotulo="Revisões concluídas (org)"
                                            valor={String(
                                                agregadas.revisoes_concluidas_total
                                            )}
                                        />
                                        <CartaoStat
                                            rotulo="Respostas registradas"
                                            valor={String(
                                                agregadas.respostas_total
                                            )}
                                        />
                                        <CartaoStat
                                            rotulo="Taxa de erro global"
                                            valor={`${Math.round(
                                                agregadas.taxa_erro_global * 100
                                            )}%`}
                                        />
                                    </GradeCartoes>
                                    <Box sx={{mt: 2}}>
                                        <Button
                                            startIcon={<DownloadOutlinedIcon />}
                                            onClick={baixarMetricas}
                                            sx={{textTransform: 'none'}}
                                        >
                                            Exportar métricas (CSV)
                                        </Button>
                                    </Box>
                                    {/* Acompanhamento por servidor. Nominal, e
                                        por isso com a finalidade dita na tela
                                        logo abaixo do título.

                                        Vem LOGO APÓS o resumo, antes das
                                        distribuições e dos rankings de erro:
                                        a primeira pergunta de quem administra
                                        é "como estão as minhas pessoas", e
                                        esta lista estava no pé da página,
                                        atrás de cinco blocos de diagnóstico —
                                        o fundador procurou e não achou. */}
                                    <Cartao style={{marginTop: 16}}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <div>
                                                <Typography fontWeight="bold">
                                                    Acompanhamento por servidor
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Nome e e-mail aparecem aqui
                                                    para a gestão da capacitação
                                                    da sua organização — saber
                                                    quem está estudando e quem
                                                    precisa de apoio. Use só
                                                    para isso.
                                                </Typography>
                                            </div>
                                            <Button
                                                startIcon={
                                                    <DownloadOutlinedIcon />
                                                }
                                                onClick={baixarServidores}
                                                disabled={exportando}
                                                sx={{textTransform: 'none'}}
                                            >
                                                {exportando
                                                    ? 'Gerando…'
                                                    : 'Exportar servidores (CSV)'}
                                            </Button>
                                        </Box>

                                        {erroExportacao && (
                                            <Alert
                                                severity="warning"
                                                sx={{mt: 2}}
                                                onClose={() =>
                                                    setErroExportacao(false)
                                                }
                                            >
                                                Não foi possível montar a lista
                                                completa agora. Nada foi baixado
                                                — tente de novo em instantes.
                                            </Alert>
                                        )}

                                        {!usuarios ? (
                                            <Box sx={{display: 'flex', mt: 2}}>
                                                <CircularProgress size={20} />
                                            </Box>
                                        ) : usuarios.itens.length === 0 ? (
                                            <Typography
                                                color="text.secondary"
                                                sx={{mt: 2}}
                                            >
                                                Ninguém da organização começou a
                                                trilha ainda.
                                            </Typography>
                                        ) : (
                                            <>
                                                <RolagemTabela>
                                                    <Table
                                                        size="small"
                                                        sx={{mt: 1}}
                                                    >
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell
                                                                    sortDirection={
                                                                        ordem.campo ===
                                                                        'nome'
                                                                            ? ordem.direcao
                                                                            : false
                                                                    }
                                                                >
                                                                    <TableSortLabel
                                                                        active={
                                                                            ordem.campo ===
                                                                            'nome'
                                                                        }
                                                                        direction={
                                                                            ordem.campo ===
                                                                            'nome'
                                                                                ? ordem.direcao
                                                                                : 'asc'
                                                                        }
                                                                        onClick={() =>
                                                                            alternarOrdem(
                                                                                'nome'
                                                                            )
                                                                        }
                                                                    >
                                                                        Servidor
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell>
                                                                    E-mail
                                                                </TableCell>
                                                                <TableCell>
                                                                    Título
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    sortDirection={
                                                                        ordem.campo ===
                                                                        'xp'
                                                                            ? ordem.direcao
                                                                            : false
                                                                    }
                                                                >
                                                                    <TableSortLabel
                                                                        active={
                                                                            ordem.campo ===
                                                                            'xp'
                                                                        }
                                                                        direction={
                                                                            ordem.campo ===
                                                                            'xp'
                                                                                ? ordem.direcao
                                                                                : 'desc'
                                                                        }
                                                                        onClick={() =>
                                                                            alternarOrdem(
                                                                                'xp'
                                                                            )
                                                                        }
                                                                    >
                                                                        XP
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    sortDirection={
                                                                        ordem.campo ===
                                                                        'dominadas_total'
                                                                            ? ordem.direcao
                                                                            : false
                                                                    }
                                                                >
                                                                    <TableSortLabel
                                                                        active={
                                                                            ordem.campo ===
                                                                            'dominadas_total'
                                                                        }
                                                                        direction={
                                                                            ordem.campo ===
                                                                            'dominadas_total'
                                                                                ? ordem.direcao
                                                                                : 'desc'
                                                                        }
                                                                        onClick={() =>
                                                                            alternarOrdem(
                                                                                'dominadas_total'
                                                                            )
                                                                        }
                                                                    >
                                                                        Dominadas
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    sortDirection={
                                                                        ordem.campo ===
                                                                        'streak_atual'
                                                                            ? ordem.direcao
                                                                            : false
                                                                    }
                                                                >
                                                                    <TableSortLabel
                                                                        active={
                                                                            ordem.campo ===
                                                                            'streak_atual'
                                                                        }
                                                                        direction={
                                                                            ordem.campo ===
                                                                            'streak_atual'
                                                                                ? ordem.direcao
                                                                                : 'desc'
                                                                        }
                                                                        onClick={() =>
                                                                            alternarOrdem(
                                                                                'streak_atual'
                                                                            )
                                                                        }
                                                                    >
                                                                        Sequência
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                                <TableCell
                                                                    sortDirection={
                                                                        ordem.campo ===
                                                                        'ultimo_dia'
                                                                            ? ordem.direcao
                                                                            : false
                                                                    }
                                                                >
                                                                    <TableSortLabel
                                                                        active={
                                                                            ordem.campo ===
                                                                            'ultimo_dia'
                                                                        }
                                                                        direction={
                                                                            ordem.campo ===
                                                                            'ultimo_dia'
                                                                                ? ordem.direcao
                                                                                : 'desc'
                                                                        }
                                                                        onClick={() =>
                                                                            alternarOrdem(
                                                                                'ultimo_dia'
                                                                            )
                                                                        }
                                                                    >
                                                                        Último
                                                                        estudo
                                                                    </TableSortLabel>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {usuariosDaPagina.map(
                                                                (u) => (
                                                                    <TableRow
                                                                        key={
                                                                            u.user_id
                                                                        }
                                                                    >
                                                                        <TableCell>
                                                                            {u.nome ?? (
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    color="text.secondary"
                                                                                    component="span"
                                                                                >
                                                                                    {
                                                                                        u.user_id
                                                                                    }
                                                                                </Typography>
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {u.email ??
                                                                                '—'}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {
                                                                                u.titulo
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {
                                                                                u.xp
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {
                                                                                u.dominadas_total
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {
                                                                                u.streak_atual
                                                                            }{' '}
                                                                            (rec.{' '}
                                                                            {
                                                                                u.streak_recorde
                                                                            }
                                                                            )
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {dataBr(
                                                                                u.ultimo_dia
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </RolagemTabela>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'flex-end',
                                                        gap: 1,
                                                        mt: 1,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {usuarios.deslocamento +
                                                            1}
                                                        –
                                                        {usuarios.deslocamento +
                                                            usuarios.itens
                                                                .length}{' '}
                                                        de {usuarios.total}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        sx={{
                                                            textTransform:
                                                                'none',
                                                        }}
                                                        disabled={
                                                            deslocamento === 0
                                                        }
                                                        onClick={() =>
                                                            setDeslocamento(
                                                                (d) =>
                                                                    Math.max(
                                                                        0,
                                                                        d -
                                                                            USUARIOS_POR_PAGINA
                                                                    )
                                                            )
                                                        }
                                                    >
                                                        Anterior
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        sx={{
                                                            textTransform:
                                                                'none',
                                                        }}
                                                        disabled={
                                                            usuarios.deslocamento +
                                                                usuarios.itens
                                                                    .length >=
                                                            usuarios.total
                                                        }
                                                        onClick={() =>
                                                            setDeslocamento(
                                                                (d) =>
                                                                    d +
                                                                    USUARIOS_POR_PAGINA
                                                            )
                                                        }
                                                    >
                                                        Próxima
                                                    </Button>
                                                </Box>

                                                {temMaisDeUmaPagina && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        component="p"
                                                        sx={{mt: 1}}
                                                    >
                                                        A ordenação vale para
                                                        esta página. A lista
                                                        completa da organização,
                                                        já ordenada, sai no CSV.
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </Cartao>

                                    <Cartao style={{marginTop: 16}}>
                                        <Typography
                                            fontWeight="bold"
                                            sx={{mb: 1}}
                                        >
                                            Distribuição de títulos (org)
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {Object.entries(
                                                agregadas.distribuicao_titulos
                                            ).map(([nome, qtd]) => (
                                                <Chip
                                                    key={nome}
                                                    label={`${nome}: ${qtd}`}
                                                />
                                            ))}
                                        </Box>
                                    </Cartao>

                                    {/* Hábito: é esta distribuição, e não o XP,
                                        que diz se o estudo virou rotina na
                                        organização. */}
                                    <Cartao style={{marginTop: 16}}>
                                        <Typography fontWeight="bold">
                                            Hábito: sequência de dias (org)
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Quantas pessoas estão com a
                                            sequência viva em cada faixa. Quem
                                            deixou a sequência quebrar não entra
                                            em faixa nenhuma — por isso a soma
                                            pode ser menor que o total de
                                            usuários.
                                        </Typography>
                                        {FAIXAS_STREAK.map((faixa) => {
                                            const qtd =
                                                agregadas.distribuicao_streak[
                                                    faixa
                                                ] ?? 0
                                            return (
                                                <LinhaFaixa key={faixa}>
                                                    <Typography variant="body2">
                                                        {faixa} dias
                                                    </Typography>
                                                    <TrilhoBarra aria-hidden>
                                                        <PreenchimentoBarra
                                                            $fracao={
                                                                qtd /
                                                                maiorFaixaStreak
                                                            }
                                                        />
                                                    </TrilhoBarra>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        align="right"
                                                    >
                                                        {qtd}
                                                    </Typography>
                                                </LinhaFaixa>
                                            )
                                        })}
                                    </Cartao>

                                    {/* Onde a organização trava: o slug cru do
                                        servidor cruzado com o catálogo do
                                        front, para o gestor ler o assunto em
                                        vez de decifrar identificador. */}
                                    <Cartao style={{marginTop: 16}}>
                                        <Typography fontWeight="bold">
                                            Onde a organização trava (seções com
                                            mais erros)
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{mb: 1}}
                                        >
                                            Só respostas de lição: as sessões de
                                            revisão misturam seções e não têm
                                            seção a declarar.
                                        </Typography>
                                        {agregadas.secoes_mais_erradas
                                            .length === 0 ? (
                                            <Typography color="text.secondary">
                                                Sem erros registrados ainda.
                                            </Typography>
                                        ) : (
                                            <RolagemTabela>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                Seção
                                                            </TableCell>
                                                            <TableCell>
                                                                Trilha
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Erros
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Respostas
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Erro
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {agregadas.secoes_mais_erradas.map(
                                                            (linha) => {
                                                                const rotulo =
                                                                    rotularSecao(
                                                                        secoes,
                                                                        linha.secao_slug
                                                                    )
                                                                return (
                                                                    <TableRow
                                                                        key={
                                                                            linha.secao_slug
                                                                        }
                                                                    >
                                                                        <TableCell>
                                                                            {
                                                                                rotulo.titulo
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {
                                                                                rotulo.trilha
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {
                                                                                linha.erros
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {
                                                                                linha.respostas
                                                                            }
                                                                        </TableCell>
                                                                        <TableCell align="right">
                                                                            {percentualBr(
                                                                                linha.erros,
                                                                                linha.respostas
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )
                                                            }
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </RolagemTabela>
                                        )}
                                    </Cartao>
                                    <Cartao style={{marginTop: 16}}>
                                        <Typography
                                            fontWeight="bold"
                                            sx={{mb: 1}}
                                        >
                                            Pílulas com mais erros (org)
                                        </Typography>
                                        {agregadas.pilulas_mais_erradas
                                            .length === 0 ? (
                                            <Typography color="text.secondary">
                                                Sem erros registrados ainda.
                                            </Typography>
                                        ) : (
                                            <RolagemTabela>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>
                                                                Pílula
                                                            </TableCell>
                                                            <TableCell>
                                                                Pergunta
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Erros
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                Respostas
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {agregadas.pilulas_mais_erradas.map(
                                                            (linha) => (
                                                                <TableRow
                                                                    key={
                                                                        linha.pilula_id
                                                                    }
                                                                >
                                                                    <TableCell>
                                                                        {
                                                                            linha.pilula_id
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {porPilula
                                                                            .get(
                                                                                linha.pilula_id
                                                                            )
                                                                            ?.pergunta.slice(
                                                                                0,
                                                                                80
                                                                            ) ??
                                                                            '—'}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {
                                                                            linha.erros
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {
                                                                            linha.respostas
                                                                        }
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </RolagemTabela>
                                        )}
                                    </Cartao>

                                    <Typography
                                        fontWeight="bold"
                                        sx={{mt: 3, mb: 0.5}}
                                    >
                                        Sua atividade neste navegador
                                    </Typography>
                                </>
                            ) : (
                                <Alert severity="info" sx={{mt: 2}}>
                                    {academyDisponivel()
                                        ? 'academy-service indisponível no momento — exibindo métricas deste navegador; reconecta sozinho.'
                                        : 'Sem backend configurado (VITE_ACADEMY_URL): métricas deste navegador. Configure o academy-service para ver o agregado de todos os usuários.'}
                                </Alert>
                            )}
                            <GradeCartoes>
                                <CartaoStat
                                    rotulo="Título atual"
                                    valor={titulo.nome}
                                />
                                <CartaoStat
                                    rotulo="XP"
                                    valor={String(progresso.xp)}
                                />
                                <CartaoStat
                                    rotulo="Pílulas dominadas"
                                    valor={`${Math.min(
                                        dominadas,
                                        totalPilulas
                                    )} / ${totalPilulas}`}
                                />
                                <CartaoStat
                                    rotulo="Lições concluídas"
                                    valor={`${licoesFeitas} / ${totalLicoes}`}
                                />
                                <CartaoStat
                                    rotulo="Sequência"
                                    valor={`${streakVigente(progresso)} dias`}
                                />
                                <CartaoStat
                                    rotulo="Recorde de dias"
                                    valor={String(progresso.streak.recorde)}
                                />
                            </GradeCartoes>

                            <Cartao style={{marginTop: 16}}>
                                <Typography fontWeight="bold" sx={{mb: 1}}>
                                    Régua de títulos (XP mínimo)
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {TITULOS.map((t) => (
                                        <Chip
                                            key={t.nome}
                                            label={`${t.nome}: ${t.xp} XP`}
                                            variant={
                                                t.nome === titulo.nome
                                                    ? 'filled'
                                                    : 'outlined'
                                            }
                                        />
                                    ))}
                                </Box>
                            </Cartao>

                            {/* Dívida EM ABERTO da própria conta (stats
                                sincronizados desde a 0004): pílula redimida na
                                revisão sai daqui. O histórico cumulativo, que
                                redenção não apaga, é a tabela da org acima. */}
                            <Cartao style={{marginTop: 16}}>
                                <Typography fontWeight="bold" sx={{mb: 1}}>
                                    Pílulas com erros em aberto (sua conta)
                                </Typography>
                                {maisErradas.length === 0 ? (
                                    <Typography color="text.secondary">
                                        Nenhuma pílula com erro em aberto.
                                    </Typography>
                                ) : (
                                    <RolagemTabela>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        Pílula
                                                    </TableCell>
                                                    <TableCell>
                                                        Pergunta
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Erros
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Respostas
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {maisErradas.map(([id, s]) => (
                                                    <TableRow key={id}>
                                                        <TableCell>
                                                            {id}
                                                        </TableCell>
                                                        <TableCell>
                                                            {porPilula
                                                                .get(id)
                                                                ?.pergunta.slice(
                                                                    0,
                                                                    80
                                                                ) ?? '—'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {s.e}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {s.t}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </RolagemTabela>
                                )}
                            </Cartao>
                        </>
                    )}

                    {papel === 'master' && aba === 'orgs' && (
                        <>
                            {!academyDisponivel() || !organizacoes ? (
                                <Alert severity="info" sx={{mt: 2}}>
                                    A visão global depende do academy-service
                                    conectado e do escopo{' '}
                                    <strong>academy:master</strong> no seu
                                    token. Sem os dois, não há o que mostrar
                                    aqui.
                                </Alert>
                            ) : (
                                <>
                                    <Typography
                                        fontWeight="bold"
                                        sx={{mt: 3, mb: 1}}
                                    >
                                        Engajamento por organização
                                    </Typography>
                                    <RolagemTabela>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        Organização
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Usuários
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Ativos hoje
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        7 dias
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        30 dias
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        XP médio
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {organizacoes.organizacoes.map(
                                                    (o) => (
                                                        <TableRow
                                                            key={
                                                                o.organization_id
                                                            }
                                                            hover
                                                            selected={
                                                                orgFiltrada ===
                                                                o.organization_id
                                                            }
                                                            onClick={() =>
                                                                setOrgFiltrada(
                                                                    orgFiltrada ===
                                                                        o.organization_id
                                                                        ? null
                                                                        : o.organization_id
                                                                )
                                                            }
                                                            sx={{
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <TableCell>
                                                                {
                                                                    o.organization_id
                                                                }
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {
                                                                    o.usuarios_total
                                                                }
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {o.ativos_hoje}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {o.ativos_7d}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {o.ativos_30d}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {numeroBr(
                                                                    o.xp_medio,
                                                                    1
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                )}
                                            </TableBody>
                                        </Table>
                                    </RolagemTabela>
                                    <Typography
                                        fontWeight="bold"
                                        sx={{mt: 3, mb: 1}}
                                    >
                                        {orgFiltrada
                                            ? `Usuários de ${orgFiltrada}`
                                            : 'Usuários de todas as organizações'}
                                        {orgFiltrada && (
                                            <Button
                                                size="small"
                                                color="inherit"
                                                onClick={() =>
                                                    setOrgFiltrada(null)
                                                }
                                                sx={{
                                                    ml: 1.5,
                                                    textTransform: 'none',
                                                }}
                                            >
                                                limpar filtro
                                            </Button>
                                        )}
                                    </Typography>
                                    {usuariosGlobais && (
                                        <RolagemTabela>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>
                                                            Organização
                                                        </TableCell>
                                                        <TableCell>
                                                            Servidor
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            XP
                                                        </TableCell>
                                                        <TableCell>
                                                            Título
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            Sequência
                                                        </TableCell>
                                                        <TableCell>
                                                            Último estudo
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {usuariosGlobais.itens.map(
                                                        (u) => (
                                                            <TableRow
                                                                key={`${u.organization_id}:${u.user_id}`}
                                                            >
                                                                <TableCell>
                                                                    {
                                                                        u.organization_id
                                                                    }
                                                                </TableCell>
                                                                <TableCell>
                                                                    {u.nome ??
                                                                        u.user_id}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {u.xp}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {u.titulo}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {
                                                                        u.streak_atual
                                                                    }
                                                                </TableCell>
                                                                <TableCell>
                                                                    {dataBr(
                                                                        u.ultimo_dia
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </RolagemTabela>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {papel === 'master' && aba === 'conteudo' && (
                        <>
                            <Alert severity="warning" sx={{mt: 2}}>
                                Alterações valem{' '}
                                <strong>neste navegador</strong> (F1). Para
                                publicar para todos: “Exportar JSON” e versionar
                                o arquivo da seção no repositório. Desativar ou
                                acrescentar pílulas muda o tamanho da seção e
                                reseta o progresso local dela.
                            </Alert>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    mt: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <TextField
                                    select
                                    size="small"
                                    label="Seção"
                                    value={slugSelecionado}
                                    onChange={(e) =>
                                        setSlugSelecionado(e.target.value)
                                    }
                                    sx={{minWidth: {xs: '100%', sm: 320}}}
                                >
                                    {secoes.map((s) => (
                                        <MenuItem key={s.slug} value={s.slug}>
                                            {s.trilha} · {s.titulo} (
                                            {s.pilulas.length})
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    size="small"
                                    label="Buscar pergunta/id"
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                    sx={{flex: 1, minWidth: 200}}
                                />
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="contained"
                                    onClick={() => {
                                        setNovaNaSecao(true)
                                        setEmEdicao({
                                            ...PILULA_VAZIA,
                                            id: `${slugSelecionado}-adm${Date.now()}`,
                                        })
                                    }}
                                    sx={{
                                        backgroundColor: COLORS.botaoFormulario,
                                        textTransform: 'none',
                                    }}
                                >
                                    Nova pílula
                                </Button>
                                <Tooltip title="Baixa o JSON da seção com as alterações aplicadas, pronto para versionar no repositório">
                                    <Button
                                        startIcon={<DownloadOutlinedIcon />}
                                        onClick={exportarSecao}
                                        sx={{textTransform: 'none'}}
                                    >
                                        Exportar JSON
                                    </Button>
                                </Tooltip>
                                <Button
                                    startIcon={<RestoreOutlinedIcon />}
                                    color="inherit"
                                    onClick={descartarAlteracoes}
                                    sx={{textTransform: 'none'}}
                                >
                                    Descartar alterações
                                </Button>
                            </Box>

                            <Cartao style={{marginTop: 16, padding: 0}}>
                                <RolagemTabela>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Pílula</TableCell>
                                                <TableCell>Pergunta</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="right">
                                                    Ações
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {listaPilulas.map((p) => {
                                                const st = statusDa(p)
                                                return (
                                                    <TableRow
                                                        key={p.id}
                                                        sx={{
                                                            opacity:
                                                                st ===
                                                                'desativada'
                                                                    ? 0.5
                                                                    : 1,
                                                        }}
                                                    >
                                                        <TableCell>
                                                            {p.id}
                                                        </TableCell>
                                                        <TableCell>
                                                            {p.pergunta.slice(
                                                                0,
                                                                90
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {st !==
                                                                'original' && (
                                                                <Chip
                                                                    size="small"
                                                                    label={st}
                                                                    color={
                                                                        st ===
                                                                        'desativada'
                                                                            ? 'default'
                                                                            : 'primary'
                                                                    }
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Tooltip title="Editar">
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setNovaNaSecao(
                                                                            false
                                                                        )
                                                                        setEmEdicao(
                                                                            {
                                                                                ...p,
                                                                            }
                                                                        )
                                                                    }}
                                                                    disabled={
                                                                        st ===
                                                                        'desativada'
                                                                    }
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </Button>
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    st ===
                                                                    'desativada'
                                                                        ? 'Reativar'
                                                                        : st ===
                                                                            'nova'
                                                                          ? 'Excluir pílula nova'
                                                                          : 'Desativar'
                                                                }
                                                            >
                                                                <Button
                                                                    size="small"
                                                                    color="inherit"
                                                                    onClick={() =>
                                                                        alternarAtivacao(
                                                                            p
                                                                        )
                                                                    }
                                                                >
                                                                    {st ===
                                                                    'desativada' ? (
                                                                        <VisibilityOutlinedIcon fontSize="small" />
                                                                    ) : (
                                                                        <VisibilityOffOutlinedIcon fontSize="small" />
                                                                    )}
                                                                </Button>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                </RolagemTabela>
                            </Cartao>
                        </>
                    )}
                </Miolo>
            </Content>

            <Dialog
                open={emEdicao !== null}
                onClose={() => {
                    setEmEdicao(null)
                    setNovaNaSecao(false)
                }}
                fullWidth
                maxWidth="md"
                fullScreen={telaPequena}
                aria-labelledby="titulo-dialog-pilula"
            >
                {emEdicao && (
                    <>
                        <DialogTitle id="titulo-dialog-pilula">
                            {novaNaSecao
                                ? 'Nova pílula'
                                : `Editar ${emEdicao.id}`}
                        </DialogTitle>
                        <DialogContent
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                pt: '8px !important',
                            }}
                        >
                            <TextField
                                label="Texto da pílula (micro-lição)"
                                multiline
                                minRows={3}
                                value={emEdicao.texto}
                                onChange={(e) =>
                                    setEmEdicao({
                                        ...emEdicao,
                                        texto: e.target.value,
                                    })
                                }
                            />
                            <TextField
                                label="Pergunta"
                                multiline
                                value={emEdicao.pergunta}
                                onChange={(e) =>
                                    setEmEdicao({
                                        ...emEdicao,
                                        pergunta: e.target.value,
                                    })
                                }
                            />
                            <RadioGroup
                                value={emEdicao.correta}
                                onChange={(e) =>
                                    setEmEdicao({
                                        ...emEdicao,
                                        correta: Number(e.target.value),
                                    })
                                }
                            >
                                {emEdicao.alternativas.map((alt, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <FormControlLabel
                                            value={i}
                                            control={
                                                <Radio
                                                    inputProps={{
                                                        'aria-label': `Alternativa ${
                                                            i + 1
                                                        } é a correta`,
                                                    }}
                                                />
                                            }
                                            label=""
                                            sx={{mr: 0}}
                                        />
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label={`Alternativa ${i + 1}${
                                                emEdicao.correta === i
                                                    ? ' (correta)'
                                                    : ''
                                            }`}
                                            value={alt}
                                            onChange={(e) => {
                                                const alternativas = [
                                                    ...emEdicao.alternativas,
                                                ]
                                                alternativas[i] = e.target.value
                                                setEmEdicao({
                                                    ...emEdicao,
                                                    alternativas,
                                                })
                                            }}
                                        />
                                    </Box>
                                ))}
                            </RadioGroup>
                            <TextField
                                label="Explicação"
                                multiline
                                value={emEdicao.explicacao}
                                onChange={(e) =>
                                    setEmEdicao({
                                        ...emEdicao,
                                        explicacao: e.target.value,
                                    })
                                }
                            />
                            <TextField
                                label="Fonte (base legal)"
                                value={emEdicao.fonte}
                                onChange={(e) =>
                                    setEmEdicao({
                                        ...emEdicao,
                                        fonte: e.target.value,
                                    })
                                }
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                color="inherit"
                                onClick={() => {
                                    setEmEdicao(null)
                                    setNovaNaSecao(false)
                                }}
                                sx={{textTransform: 'none'}}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                disabled={
                                    !emEdicao.pergunta.trim() ||
                                    !emEdicao.texto.trim() ||
                                    !emEdicao.explicacao.trim() ||
                                    !emEdicao.fonte.trim() ||
                                    emEdicao.alternativas.some((a) => !a.trim())
                                }
                                onClick={() => salvarPilula(emEdicao)}
                                sx={{
                                    backgroundColor: COLORS.botaoFormulario,
                                    textTransform: 'none',
                                }}
                            >
                                Salvar
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Page>
    )
}
